import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import {
  FaArrowUp,
  FaArrowDown,
  FaArrowLeft,
  FaArrowRight,
  FaArrowsAltH,
  FaArrowsAltV,
  FaRegClock,
  FaRegCalendarAlt,
} from "react-icons/fa";
import { AiOutlinePlus, AiOutlineMinus } from "react-icons/ai";
import { BiImageAdd } from "react-icons/bi";
import { IoChevronForward, IoChevronDown } from "react-icons/io5";
import { HiRefresh } from "react-icons/hi";
import { LuPipette } from "react-icons/lu";
import useDialogState, { Loader } from "../../hook";
import { notifyError, notifySuccess } from "../../hook/notification";
import LiveClockService from "../../services/liveclock.service";
import FontService from "../../services/font.service";
import CategoryService from "../../services/category.service";
import CollectionService from "../../services/collection.service";
import WallpaperCanvasPreview from "../../../components/WallpaperCanvasPreview";
import { buildDateStr } from "../../../renderer/ClockLayer";
import flatpickr from "flatpickr";
import "flatpickr/dist/themes/dark.css";

const _injectedFonts = new Set();

const sanitizeTags = (tags) =>
  Array.isArray(tags)
    ? tags.filter(
        (tag) =>
          tag != null &&
          String(tag).trim() !== "" &&
          String(tag).trim().toLowerCase() !== "null"
      )
    : [];

// ─── Dropdown ────────────────────────────────────────────────────────────────
const Dropdown = ({ options, onSelect, selectedValue }) => {
  const menuRef = useRef();
  const [toggleShow, setToggleShow] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setToggleShow(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <div className="flex flex-col gap-2 relative" ref={menuRef}>
      <div
        onClick={() => setToggleShow((p) => !p)}
        className="input cursor-pointer"
      >
        {!selectedValue ? "Select" : selectedValue}
      </div>
      <div
        className={`${toggleShow ? "block" : "hidden"
          } bg-gray-800 rounded-6 overflow-hidden absolute top-[40px] w-full z-70 min-h-10 max-h-52 h-auto overflow-y-auto`}
      >
        {options?.length > 0 ? (
          options.map((opt, i) => (
            <p
              key={i}
              onClick={() => {
                onSelect(opt.label, opt.value);
                setToggleShow(false);
              }}
              className="bg-transparent cursor-pointer hover:bg-gray-600 p-2 text-white"
            >
              {opt.label}
            </p>
          ))
        ) : (
          <p className="bg-transparent cursor-pointer hover:bg-gray-600 p-2 text-white">
            No data found
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Slider ──────────────────────────────────────────────────────────────────
const Slider = ({ label, value, onChange, onReset, min = 0, max = 100, resetValue = 50 }) => {
  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between">
        <span className="text-white text-xs font-medium">{label}</span>
        <div className="flex items-center gap-1">
          <span className="text-white text-xs">{value}%</span>
          <HiRefresh
            className="text-white text-sm cursor-pointer"
            onClick={() => onReset ? onReset() : onChange(resetValue)}
          />
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1 appearance-none rounded-full cursor-pointer"
        style={{
          background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${value}%, #4B5563 ${value}%, #4B5563 100%)`,
        }}
      />
    </div>
  );
};

// ─── SliderRaw (0–1 range for gradient stops) ────────────────────────────────
const SliderRaw = ({ label, value, onChange, onReset, min = 0, max = 1, step = 0.01, resetValue }) => {
  const pct = Math.round(((value - min) / (max - min)) * 100);
  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between">
        <span className="text-white text-xs font-medium">{label}</span>
        <div className="flex items-center gap-1">
          <span className="text-white text-xs">{(value ?? 0).toFixed(2)}</span>
          <HiRefresh
            className="text-white text-sm cursor-pointer"
            onClick={() => onReset ? onReset() : onChange(resetValue !== undefined ? resetValue : max)}
          />
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1 appearance-none rounded-full cursor-pointer"
        style={{
          background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${pct}%, #4B5563 ${pct}%, #4B5563 100%)`,
        }}
      />
    </div>
  );
};

// ─── AngleSlider ─────────────────────────────────────────────────────────────
const AngleSlider = ({ label, value, onChange }) => {
  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between">
        <span className="text-white text-xs font-medium">{label}</span>
        <div className="flex items-center gap-1">
          <span className="text-white text-xs">{value}°</span>
          <HiRefresh
            className="text-white text-sm cursor-pointer"
            onClick={() => onChange(0)}
          />
        </div>
      </div>
      <input
        type="range"
        min={0}
        max={360}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1 appearance-none rounded-full cursor-pointer"
        style={{
          background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${(value / 360) * 100}%, #4B5563 ${(value / 360) * 100}%, #4B5563 100%)`,
        }}
      />
    </div>
  );
};

// ─── Toggle ──────────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${checked ? "bg-blue" : "bg-gray-600"
      }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${checked ? "translate-x-6" : "translate-x-1"
        }`}
    />
  </button>
);

// ─── ColorPickerPopup — HSV 2D picker with hue bar + hex input ────────────────
const ColorPickerPopup = ({ color, onChange, size, showPipette = false }) => {
  const [open, setOpen] = useState(false);
  const [hue, setHue] = useState(0);
  const [sat, setSat] = useState(1);
  const [val, setVal] = useState(1);
  const [hex, setHex] = useState(color);
  const ref = useRef();
  const squareRef = useRef();

  // Convert HSV → hex
  const hsvToHex = (h, s, v) => {
    const f = (n) => {
      const k = (n + h / 60) % 6;
      return v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
    };
    const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, "0");
    return `#${toHex(f(5))}${toHex(f(3))}${toHex(f(1))}`;
  };

  // Convert hex → HSV
  const hexToHsv = (hexStr) => {
    const r = parseInt(hexStr.slice(1, 3), 16) / 255;
    const g = parseInt(hexStr.slice(3, 5), 16) / 255;
    const b = parseInt(hexStr.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
      if (max === r) h = ((g - b) / d) % 6;
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h = Math.round(h * 60);
      if (h < 0) h += 360;
    }
    return { h, s: max === 0 ? 0 : d / max, v: max };
  };

  // Sync internal state when color prop changes externally
  useEffect(() => {
    if (/^#[0-9a-fA-F]{6}$/.test(color)) {
      const { h, s, v } = hexToHsv(color);
      setHue(h); setSat(s); setVal(v);
      setHex(color);
    }
  }, [color]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const emitColor = (h, s, v) => {
    const newHex = hsvToHex(h, s, v);
    setHex(newHex);
    onChange(newHex);
  };

  // Drag on the 2D square
  const handleSquarePointer = (e) => {
    const rect = squareRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    const newSat = x;
    const newVal = 1 - y;
    setSat(newSat); setVal(newVal);
    emitColor(hue, newSat, newVal);
  };

  const onSquareMouseDown = (e) => {
    handleSquarePointer(e);
    const move = (ev) => handleSquarePointer(ev);
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const handleHexInput = (v) => {
    setHex(v);
    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
      const { h, s, v: bv } = hexToHsv(v);
      setHue(h); setSat(s); setVal(bv ?? val);
      onChange(v);
    }
  };

  const rgbaToHex = (rgba) => {
    const m = rgba.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (!m) return null;
    return "#" + [m[1], m[2], m[3]]
      .map((n) => Math.min(255, Math.max(0, parseInt(n, 10))).toString(16).padStart(2, "0"))
      .join("");
  };

  const handlePipette = async () => {
    if (!window.EyeDropper) return setOpen((p) => !p);
    try {
      const result = await new window.EyeDropper().open();
      const hex = /^#[0-9a-f]{6}$/i.test(result.sRGBHex)
        ? result.sRGBHex.toLowerCase()
        : rgbaToHex(result.sRGBHex);
      if (hex) { setHex(hex); onChange(hex); const { h, s, v: bv } = hexToHsv(hex); setHue(h); setSat(s); setVal(bv); }
    } catch { /* user cancelled */ }
  };

  const circleClass = size === "lg"
    ? "w-12 h-12 rounded-full border-2 border-white flex-shrink-0 shadow cursor-pointer"
    : "w-10 h-10 rounded-full border-2 border-white flex-shrink-0 shadow cursor-pointer";

  const pureHue = hsvToHex(hue, 1, 1);
  const selectorX = `${sat * 100}%`;
  const selectorY = `${(1 - val) * 100}%`;

  return (
    <div className="relative flex items-center gap-2" ref={ref}>
      {/* Trigger circle */}
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={circleClass}
        style={{ backgroundColor: color }}
      />
      {/* Optional Pipette Icon trigger */}
      {showPipette && (
        <LuPipette
          className="text-white text-lg cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
          onClick={handlePipette}
        />
      )}

      {/* Popup */}
      {open && (
        <div className="absolute left-0 top-[calc(100%+6px)] z-[200] w-56 bg-[#1e1e1e] rounded-10 shadow-2xl border border-gray-700 p-2">

          {/* 2D Saturation/Value square */}
          <div
            ref={squareRef}
            onMouseDown={onSquareMouseDown}
            className="relative w-full rounded-6 overflow-hidden mb-2 cursor-crosshair"
            style={{ height: "140px" }}
          >
            {/* Base hue layer */}
            <div className="absolute inset-0" style={{ backgroundColor: pureHue }} />
            {/* White → transparent (left→right = saturation) */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #fff, transparent)" }} />
            {/* Black → transparent (top→bottom = value) */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent, #000)" }} />
            {/* Selector dot */}
            <div
              className="absolute w-3 h-3 rounded-full border-2 border-white shadow pointer-events-none"
              style={{
                left: selectorX,
                top: selectorY,
                transform: "translate(-50%, -50%)",
                backgroundColor: hsvToHex(hue, sat, val),
              }}
            />
          </div>

          {/* Preview circle + Hue bar row */}
          <div className="flex items-center gap-2 mb-2">
            {/* Color preview circle */}
            <div
              className="w-5 h-5 rounded-full border-2 border-gray-500 flex-shrink-0"
              style={{ backgroundColor: color }}
            />
            {/* Hue slider */}
            <div className="flex-1 relative h-3 rounded-full overflow-hidden" style={{ background: "linear-gradient(to right,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00)" }}>
              <input
                type="range" min={0} max={360} step={1} value={hue}
                onChange={(e) => {
                  const h = Number(e.target.value);
                  setHue(h);
                  emitColor(h, sat, val);
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>
          </div>

          {/* Hex input */}
          <div>
            <div className="text-gray-400 text-[10px] mb-0.5">Hex</div>
            <input
              type="text"
              value={hex}
              onChange={(e) => handleHexInput(e.target.value)}
              className="w-full bg-[#2a2a2a] text-white text-xs rounded-6 px-2 py-1 font-mono border border-gray-600 focus:outline-none"
              maxLength={7}
            />
          </div>
        </div>
      )}
    </div>
  );
};


const FontPopup = ({ selectedValue, onSelect, extraCategories = [] }) => {
  const [open, setOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const ref = useRef();
  const listContainerRef = useRef(null);
  const selectedFontRef = useRef(null);

  // Use only API categories (no static fonts)
  const allCategories = extraCategories;

  // Resolve the currently selected font label
  const allFonts = allCategories.flatMap((c) => c.fonts);
  const current = allFonts.find((f) => f.value === selectedValue) || allFonts[0] || { label: "Select font", value: "" };

  const getCategoryForFont = (val) => {
    if (!val) return null;
    return allCategories.find((c) => c.fonts?.some((f) => f.value === val))?.category || null;
  };

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Scroll down to the selected font in the container when opened or category active
  useEffect(() => {
    if (open && activeCat && selectedFontRef.current && listContainerRef.current) {
      const timer = setTimeout(() => {
        const container = listContainerRef.current;
        const elem = selectedFontRef.current;
        if (!container || !elem) return;

        const elemTop = elem.offsetTop;
        const elemBottom = elemTop + elem.offsetHeight;
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;

        if (elemTop < containerTop || elemBottom > containerBottom) {
          container.scrollTo({
            top: Math.max(0, elemTop - container.clientHeight / 2 + elem.offsetHeight / 2),
            behavior: "smooth",
          });
        }
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [open, activeCat, selectedValue]);

  const handleToggle = () => {
    setOpen((prev) => {
      const willOpen = !prev;
      if (willOpen) {
        // Automatically default directly inside the selected font's category (or previously active category)
        const fontCat = getCategoryForFont(selectedValue);
        setActiveCat(fontCat || activeCat || null);
      }
      return willOpen;
    });
  };

  const catFonts = activeCat
    ? (allCategories.find((c) => c.category === activeCat)?.fonts || [])
    : [];

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center justify-between bg-gray-700 rounded-6 px-3 py-2 text-white text-sm"
      >
        <span>{current.label}</span>
        <IoChevronForward
          className={`text-gray-400 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>

      {/* Popup */}
      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-70 bg-[#1a2233] rounded-10 shadow-2xl border border-gray-700">
          {!activeCat ? (
            /* Category list */
            <div className="max-h-52 overflow-y-auto">
              {allCategories.map((cat) => (
                <button
                  key={cat.category}
                  type="button"
                  onClick={() => setActiveCat(cat.category)}
                  className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-700/50 last:border-b-0 hover:bg-gray-700/60 transition-colors text-white text-sm"
                >
                  <span>{cat.category}</span>
                  <IoChevronForward className="text-gray-400 text-xs" />
                </button>
              ))}
            </div>
          ) : (
            /* Font list for selected category */
            <div>
              {/* Back header */}
              <button
                type="button"
                onClick={() => setActiveCat(null)}
                className="w-full flex items-center gap-2 px-4 py-2 border-b border-gray-700 text-gray-400 text-xs hover:bg-gray-700/40 transition-colors"
              >
                <IoChevronForward className="rotate-180" />
                <span>{activeCat}</span>
              </button>
              <div className="max-h-48 overflow-y-auto" ref={listContainerRef}>
                {catFonts.map((font) => {
                  const isSelected = selectedValue === font.value;
                  return (
                    <button
                      key={font.value}
                      ref={isSelected ? selectedFontRef : null}
                      type="button"
                      onClick={() => {
                        onSelect({ id: font.id, name: font.value });
                        setOpen(false);
                        setActiveCat(activeCat || getCategoryForFont(font.value));
                      }}
                      className={`w-full text-left px-4 py-3 border-b border-gray-700/50 last:border-b-0 hover:bg-gray-700/60 transition-colors ${isSelected ? "bg-gray-700/40" : ""}`}
                    >
                      <span className="block text-white text-xl leading-none mb-0.5" style={{ fontFamily: `"${font.value}", sans-serif` }}>
                        05:07
                      </span>
                      <span className="text-gray-400 text-xs">{font.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── FormatDropdown — styled dropdown with visible arrow for date format ──────
const FormatDropdown = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = options.find((o) => o.value === value) || options[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between bg-gray-700 border border-gray-600 rounded-6 px-3 py-2 text-white text-sm cursor-pointer"
      >
        <span>{current?.label || "Format"}</span>
        <IoChevronForward className={`text-gray-400 transition-transform flex-shrink-0 ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+2px)] z-[200] bg-gray-800 rounded-6 border border-gray-600 max-h-52 overflow-y-auto shadow-2xl">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-600 transition-colors ${value === opt.value ? "text-white bg-gray-700/50" : "text-gray-300"}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── ClockTopBar — shared across all 3 pages ─────────────────────────────────
const ClockTopBar = ({
  clockSettings,
  setClock,
  activeTab,
  setActiveTab,
  moveUp,
  moveDown,
  moveLeft,
  moveRight,
  apiFontCategories,
}) => (
  <>
    {/* Clock / Date tabs — outside container */}
    <div className="flex items-center justify-start gap-6 mb-3">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name="clockTab"
          checked={activeTab === "clock"}
          onChange={() => setActiveTab("clock")}
          className="accent-blue"
        />
        <span className="text-white font-medium">Clock</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name="clockTab"
          checked={activeTab === "date"}
          onChange={() => setActiveTab("date")}
          className="accent-blue"
        />
        <span className="text-white font-medium">Date</span>
      </label>
    </div>

    {/* Tab content container */}
    <div className="bg-gray-800 rounded-10 p-4 mb-4 h-60">
      {activeTab === "date" ? (
        <>
          {/* Date toggle — always interactive */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-white text-sm font-medium">Date</span>
            <Toggle
              checked={clockSettings.dateEnabled}
              onChange={(v) => setClock("dateEnabled", v)}
            />
          </div>

          {/* FontPopup + FormatDropdown — greyed out when toggle is OFF */}
          <div className={`transition-opacity ${clockSettings.dateEnabled ? "opacity-100 pointer-events-auto" : "opacity-40 pointer-events-none select-none"}`}>
            <div className="mb-3">
              <FontPopup
                selectedValue={clockSettings.dateFontFamily}
                onSelect={({ id, name }) => { setClock("dateFontFamily", name); setClock("dateFontId", id); }}
                extraCategories={apiFontCategories}
              />
            </div>
            <div className="mb-3">
              <FormatDropdown
                options={DATE_FORMAT_OPTIONS}
                value={clockSettings.dateFormat}
                onChange={(val) => setClock("dateFormat", val)}
              />
            </div>
            {/* Date color pickers */}
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-center gap-0.5">
                <ColorPickerPopup color={clockSettings.dateColor} onChange={(val) => setClock("dateColor", val)} size="lg" showPipette />
                <span className="text-white text-xs font-mono">{clockSettings.dateColor}</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Font popup */}
          <div className="mb-3">
            <FontPopup
              selectedValue={clockSettings.clockFontFamily}
              onSelect={({ id, name }) => { setClock("clockFontFamily", name); setClock("clockFontId", id); }}
              extraCategories={apiFontCategories}
            />
          </div>

          {/* 12Hr / 24Hr / sec */}
          <div className="flex items-center gap-1.5 mb-2">
            <button
              type="button"
              onClick={() => {
                setClock("is24Hr", false);
                const cur = clockSettings.previewTime || clockSettings.preveiwTime;
                if (cur) {
                  const [h, m] = cur.split(":");
                  const numH = parseInt(h, 10);
                  if (!isNaN(numH) && numH >= 12) {
                    const newH = String(numH % 12 || 12).padStart(2, "0");
                    setClock("previewTime", `${newH}:${m || "00"}`);
                  }
                }
              }}
              className={`px-3 py-1.5 rounded-6 text-xs font-medium border ${!clockSettings.is24Hr
                ? "bg-blue border-blue text-white"
                : "bg-gray-700 border-gray-600 text-gray-300"
                }`}
            >
              12 Hr
            </button>
            <button
              type="button"
              onClick={() => {
                setClock("is24Hr", true);
                const cur = clockSettings.previewTime || clockSettings.preveiwTime;
                if (cur) {
                  const [h, m] = cur.split(":");
                  const numH = parseInt(h, 10);
                  if (!isNaN(numH) && numH > 0 && numH < 12) {
                    const newH = String(numH + 12).padStart(2, "0");
                    setClock("previewTime", `${newH}:${m || "00"}`);
                  }
                }
              }}
              className={`px-3 py-1.5 rounded-6 text-xs font-medium border ${clockSettings.is24Hr
                ? "bg-blue border-blue text-white"
                : "bg-gray-700 border-gray-600 text-gray-300"
                }`}
            >
              24 Hr
            </button>
            <button
              type="button"
              onClick={() => setClock("showSeconds", !clockSettings.showSeconds)}
              className={`px-3 py-1.5 rounded-6 text-xs font-medium border ${clockSettings.showSeconds
                ? "bg-blue border-blue text-white"
                : "bg-gray-700 border-gray-600 text-gray-300"
                }`}
            >
              sec
            </button>
            <button
              type="button"
              onClick={() => setClock("showClockHorizontal", true)}
              className={`w-8 h-8 flex items-center justify-center rounded-6 border ${clockSettings.showClockHorizontal
                ? "bg-blue border-blue text-white"
                : "bg-gray-700 border-gray-600 text-gray-300"
                }`}
              title="Horizontal"
            >
              <FaArrowsAltH className="text-xs" />
            </button>
            <button
              type="button"
              onClick={() => { setClock("showClockHorizontal", false); setClock("showColon", false); }}
              className={`w-8 h-8 flex items-center justify-center rounded-6 border ${!clockSettings.showClockHorizontal
                ? "bg-blue border-blue text-white"
                : "bg-gray-700 border-gray-600 text-gray-300"
                }`}
              title="Vertical"
            >
              <FaArrowsAltV className="text-xs" />
            </button>
            {clockSettings.showClockHorizontal && (
              <button
                type="button"
                onClick={() => setClock("showColon", !clockSettings.showColon)}
                className={`px-3 py-1.5 rounded-6 text-xs font-medium border ${clockSettings.showColon
                  ? "bg-blue border-blue text-white"
                  : "bg-gray-700 border-gray-600 text-gray-300"
                  }`}
              >
                :
              </button>
            )}
          </div>

          {/* Direction arrows */}
          <div className="flex items-center gap-1.5 mb-2">
            <button type="button" onClick={moveLeft} className="w-8 h-8 flex items-center justify-center rounded-6 bg-blue text-white hover:bg-blue/80">
              <FaArrowLeft className="text-xs" />
            </button>
            <button type="button" onClick={moveUp} className="w-8 h-8 flex items-center justify-center rounded-6 bg-blue text-white hover:bg-blue/80">
              <FaArrowUp className="text-xs" />
            </button>
            <button type="button" onClick={moveDown} className="w-8 h-8 flex items-center justify-center rounded-6 bg-blue text-white hover:bg-blue/80">
              <FaArrowDown className="text-xs" />
            </button>
            <button type="button" onClick={moveRight} className="w-8 h-8 flex items-center justify-center rounded-6 bg-blue text-white hover:bg-blue/80">
              <FaArrowRight className="text-xs" />
            </button>
          </div>

          {/* +/− size | color1 | color2 */}
          <div className="flex items-center gap-3 mb-1">
            <button
              type="button"
              onClick={() => setClock("clockFontSize", Math.min(100, clockSettings.clockFontSize + 5))}
              className="w-8 h-8 flex items-center justify-center rounded-6 bg-gray-700 text-white hover:bg-gray-600 flex-shrink-0"
            >
              <AiOutlinePlus className="text-sm" />
            </button>
            <button
              type="button"
              onClick={() => setClock("clockFontSize", Math.max(1, clockSettings.clockFontSize - 5))}
              className="w-8 h-8 flex items-center justify-center rounded-6 bg-gray-700 text-white hover:bg-gray-600 flex-shrink-0"
            >
              <AiOutlineMinus className="text-sm" />
            </button>
            <div className="flex flex-col items-center gap-0.5">
              <ColorPickerPopup color={clockSettings.clockColor} onChange={(val) => setClock("clockColor", val)} size="lg" showPipette />
              <span className="text-white text-xs font-mono">{clockSettings.clockColor}</span>
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <ColorPickerPopup color={clockSettings.clockColor2} onChange={(val) => setClock("clockColor2", val)} size="lg" showPipette />
              <span className="text-white text-xs font-mono">{clockSettings.clockColor2}</span>
            </div>
          </div>
        </>
      )}
    </div>
  </>
);
// ─── Dot Pagination ──────────────────────────────────────────────────────────
const DotPagination = ({ total, active, onChange }) => (
  <div className="flex items-center justify-center gap-2 py-4">
    {Array.from({ length: total }).map((_, i) => (
      <button
        key={i}
        type="button"
        onClick={() => onChange(i)}
        className={`rounded-full transition-all duration-200 ${i === active
          ? "w-4 h-4 bg-white"
          : "w-3 h-3 bg-gray-600 hover:bg-gray-400"
          }`}
      />
    ))}
  </div>
);

const getDefaultPreviewTime = () => {
  try {
    const saved = localStorage.getItem("admin_default_clock_time");
    if (saved) return saved;
  } catch {}
  return "20:45";
};

const getDefaultPreviewDate = () => {
  try {
    const saved = localStorage.getItem("admin_default_clock_date");
    if (saved) return saved;
  } catch {}
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// ─── Time Dropdown with Sleek Thin Scrollbar ─────────────────────────────────
const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const HOURS_24 = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

const TimeDropdown = ({ value, options, onChange, title }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && listRef.current) {
      const selectedEl = listRef.current.querySelector('[data-selected="true"]');
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        title={title}
        className="flex items-center justify-between gap-1 bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold rounded px-2 py-1 border border-gray-600/60 cursor-pointer outline-none focus:border-blue transition-colors min-w-[48px]"
      >
        <span>{value}</span>
        <IoChevronDown className={`text-gray-400 text-[10px] transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div
          ref={listRef}
          className="absolute top-full left-0 mt-1 z-50 bg-gray-800 border border-gray-600/80 rounded-6 shadow-2xl max-h-48 overflow-y-auto custom-thin-scrollbar min-w-[54px] py-1"
        >
          {options.map((opt) => {
            const isSelected = opt === value;
            return (
              <div
                key={opt}
                data-selected={isSelected}
                onClick={() => {
                  onChange(opt);
                  setIsOpen(false);
                }}
                className={`px-2.5 py-1 text-xs font-semibold cursor-pointer text-center transition-colors ${
                  isSelected
                    ? "bg-blue text-white"
                    : "text-gray-200 hover:bg-gray-700 hover:text-white"
                }`}
              >
                {opt}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SECONDS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

const LiveClockTimePicker = ({ value, is24Hr, showSeconds, onChange }) => {
  const curTime = value || (is24Hr ? (showSeconds ? "20:45:00" : "20:45") : (showSeconds ? "08:45:00" : "08:45"));
  const parts = curTime.split(":");
  let numH = parseInt(parts[0], 10);
  if (isNaN(numH)) numH = is24Hr ? 20 : 8;
  const minuteVal = (parts[1] || "45").padStart(2, "0");
  const secondVal = (parts[2] || "00").padStart(2, "0");

  const h12 = String(numH % 12 || 12).padStart(2, "0");
  const h24 = String(numH).padStart(2, "0");

  const hoursList = is24Hr ? HOURS_24 : HOURS_12;
  const currentHourDisplay = is24Hr ? h24 : h12;

  const handleHourChange = (newH) => {
    if (showSeconds) {
      onChange(`${newH}:${minuteVal}:${secondVal}`);
    } else {
      onChange(`${newH}:${minuteVal}`);
    }
  };

  const handleMinuteChange = (newM) => {
    if (showSeconds) {
      onChange(`${currentHourDisplay}:${newM}:${secondVal}`);
    } else {
      onChange(`${currentHourDisplay}:${newM}`);
    }
  };

  const handleSecondChange = (newS) => {
    onChange(`${currentHourDisplay}:${minuteVal}:${newS}`);
  };

  return (
    <div className={`${showSeconds ? "w-[260px]" : "w-[190px]"} h-[38px] flex items-center justify-center gap-2.5 px-3 bg-gray-700/60 rounded-6 border border-gray-600/50 select-none transition-all`}>
      {/* Hour dropdown */}
      <TimeDropdown
        value={currentHourDisplay}
        options={hoursList}
        onChange={handleHourChange}
        title={is24Hr ? "Select Hour (00–23)" : "Select Hour (01–12)"}
      />

      <span className="text-gray-400 font-bold text-sm">:</span>

      {/* Minute dropdown */}
      <TimeDropdown
        value={minuteVal}
        options={MINUTES}
        onChange={handleMinuteChange}
        title="Select Minute (00–59)"
      />

      {showSeconds && (
        <>
          <span className="text-gray-400 font-bold text-sm">:</span>
          {/* Second dropdown */}
          <TimeDropdown
            value={secondVal}
            options={SECONDS}
            onChange={handleSecondChange}
            title="Select Second (00–59)"
          />
        </>
      )}
    </div>
  );
};

// ─── Date Picker using flatpickr ─────────────────────────────────────────────
const LiveClockDatePicker = ({ value, onChange }) => {
  const inputRef = useRef(null);
  const fpRef = useRef(null);

  useEffect(() => {
    if (!inputRef.current) return;
    fpRef.current = flatpickr(inputRef.current, {
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "D j M",
      altInputClass: "bg-transparent text-white text-sm outline-none cursor-pointer w-full font-medium",
      defaultDate: value || undefined,
      onValueUpdate: (_dates, dateStr) => {
        if (dateStr) onChange(dateStr);
      },
      onChange: (_dates, dateStr) => {
        if (dateStr) onChange(dateStr);
      },
    });

    return () => {
      fpRef.current?.destroy();
      fpRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (fpRef.current && value) {
      if (fpRef.current.input.value !== value) {
        fpRef.current.setDate(value, false, "Y-m-d");
      }
    }
  }, [value]);

  return (
    <div
      onClick={(e) => {
        if (e.target !== fpRef.current?.altInput && e.target !== fpRef.current?.input) {
          fpRef.current?.toggle();
        }
      }}
      className="w-[190px] h-[38px] relative flex items-center justify-between bg-gray-700/60 hover:bg-gray-700 px-3 rounded-6 border border-gray-600/50 cursor-pointer select-none"
    >
      <input
        ref={inputRef}
        type="text"
        readOnly
        className="bg-transparent text-white text-sm outline-none cursor-pointer w-full font-medium"
      />
      <IoChevronDown className="text-gray-400 text-xs ml-2 pointer-events-none shrink-0" />
    </div>
  );
};

const DEFAULT_CLOCK = {
  is24Hr: false,
  staticTime: false,
  previewTime: getDefaultPreviewTime(),
  preveiwTime: getDefaultPreviewTime(),
  previewDate: getDefaultPreviewDate(),
  preveiwDate: getDefaultPreviewDate(),
  showSeconds: false,
  showMinutes: true,
  showHours: true,
  showClockHorizontal: true,
  showColon: false,
  hoursForward: false,
  minutesForward: false,
  secondsForward: false,
  // Clock-specific
  clockFontSize: 35,
  clockOpacity: 100,
  clockHPos: 50,
  clockVPos: 45,
  clockLineSpacing: 0,
  clockLetterSpacing: 0,
  clockColor: "#ffffff",
  clockColor2: "#ffffff",
  clockFontFamily: "",
  clockFontId: null,
  clockRotation: 0,
  // Date-specific
  dateFontSize: 40,
  dateOpacity: 100,
  dateHPos: 50,
  dateVPos: 16,
  dateColor: "#ffffff",
  dateColor2: "#ffffff",
  dateFontFamily: "Barlow Bold",
  dateFontId: null,
  dateRotation: 0,
  // Gradient (clock only)
  gradientEnabled: false,
  gradientType: "linear",
  gradAngle: 0,
  gradStop1: 0.0,
  gradStop2: 1.0,
  // Transform panel
  perspRotX: 0,
  perspRotY: 0,
  perspRotZ: 0,
  hStretch: 1.0,
  vStretch: 1.0,
  hSkew: 0,
  vSkew: 0,
  // Effects panel
  borderEnabled: false,
  fillBorderEnabled: true,
  clockBorderTarget: ["Full"],
  borderColor: "#ffffff",
  borderWidth: 1.0,
  borderOpacity: 1.0,
  // Shadow panel
  shadowEnabled: false,
  shadowColor: "#ffffff",
  shadowHOffset: 0.0,
  shadowVOffset: 0.0,
  shadowBlur: 0.0,
  shadowOpacity: 1.0,
  // Date panel
  dateEnabled: true,
  dateFormat: "Day Date Month",
};

const BORDER_TARGET_OPTIONS = [
  { value: "Full", label: "Full Clock" },
  { value: "Hours", label: "Hours Only" },
  { value: "Minutes", label: "Minutes Only" },
  { value: "Seconds", label: "Seconds Only" },
];

const DATE_FORMAT_OPTIONS = [
  { label: "Day Date Month", value: "Day Date Month" },
  { label: "Day Month Year", value: "Day Month Year" },
  { label: "Full Date", value: "Full Date" },
  { label: "Short Date", value: "Short Date" },
  { label: "MM/DD/YYYY", value: "MM/DD/YYYY" },
  { label: "DD/MM/YYYY", value: "DD/MM/YYYY" },
  { label: "YYYY-MM-DD", value: "YYYY-MM-DD" },
  { label: "Month Day", value: "Month Day" },
  { label: "Day Month", value: "Day Month" },
  { label: "Short M/D", value: "Short M/D" },
  { label: "Day Only", value: "Day Only" },
  { label: "Date Only", value: "Date Only" },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const LiveClock = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef();
  const stateEditId = location.state?.imageId || location.state?.id;
  const queryEditId = new URLSearchParams(location.search).get("id");
  const editRecordId = stateEditId || queryEditId;

  // Metadata state
  const [showVal, setShowVal] = useState("NON PREMIUM");
  const [showVal2, setShowVal2] = useState("");
  const [showVal3, setShowVal3] = useState("");
  const [categoryData, setCategoryData] = useState([]);
  const [collectionData, setCollectionData] = useState([]);
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [selectedColId, setSelectedColId] = useState(null);
  const [open, setOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [notification, setNotification] = useState(false);

  // Clock settings (always reset to defaults on open)
  const [clockSettings, setClockSettings] = useState({ ...DEFAULT_CLOCK });

  // File upload state
  const bgFileInput = useRef(null);
  const fgFileInput = useRef(null);
  const initialClockSettings = useRef(null);
  const lastLoadedKey = useRef(null);
  const wallpaperPreviewRef = useRef(null);
  const [bgFile, setBgFile] = useState(null);
  const [fgFile, setFgFile] = useState(null);
  const [bgError, setBgError] = useState("");
  const [fgError, setFgError] = useState("");
  const [bgFileName, setBgFileName] = useState("");
  const [fgPreviewUrl, setFgPreviewUrl] = useState(null);

  // UI state
  const [activeTab, setActiveTab] = useState("clock"); // "clock" | "date"
  const [activePage, setActivePage] = useState(0); // 0=metadata, 1=quick+gradient
  const [bgImage, setBgImage] = useState(null);
  const [apiFontCategories, setApiFontCategories] = useState([]);

  // ── Fetch API fonts and inject @font-face rules ───────────────────────────
  useEffect(() => {
    FontService.getAllFonts().then((res) => {
      const groups = res?.data?.apiresponse?.data || [];
      // Inject @font-face for each font so it renders in the dropdown + canvas
      groups.forEach((group) => {
        group.fonts.forEach((font) => {
          if (!font.fontUrl) return;
          const familyName = font.fontName;
          if (!_injectedFonts.has(familyName)) {
            _injectedFonts.add(familyName);
            // Let the browser detect the uploaded font format (TTF, OTF, WOFF, etc.).
            const style = document.createElement("style");
            style.textContent = `@font-face { font-family: '${familyName}'; src: url('${font.fontUrl}'); font-weight: normal; font-style: normal; }`;
            document.head.appendChild(style);

            // 2. Active JS FontFace load for instant availability in canvas & dropdown
            try {
              const fontFace = new FontFace(familyName, `url("${font.fontUrl}")`);
              fontFace.load().then((loadedFace) => {
                document.fonts.add(loadedFace);
                setApiFontCategories((prev) => [...prev]);
              }).catch(() => { });
            } catch (e) { }
          }
        });
      });
      // Convert to FontPopup format: { category, fonts: [{ label, value, id }] }
      const converted = groups.map((group) => ({
        category: group.category,
        fonts: group.fonts.map((font) => ({
          label: font.fontName,
          value: font.fontName,
          id: font.id,
        })),
      }));
      setApiFontCategories(converted);

      // Apply default font: prefer "Calcio", fall back to first available font
      const allFonts = groups.flatMap((g) => g.fonts);
      if (allFonts.length === 0) return;
      const defaultFont = allFonts.find((f) => f.fontName === "Calcio") || allFonts[0];
      // Load the font then apply — only if no font has been set yet (new wallpaper)
      document.fonts.load(`16px "${defaultFont.fontName}"`).finally(() => {
        setClockSettings((prev) => {
          if (prev.clockFontId == null) {
            return { ...prev, clockFontFamily: defaultFont.fontName, clockFontId: defaultFont.id };
          }
          return prev;
        });
      });

      // Apply default date font: prefer "Barlow Bold", fall back to first available font
      const defaultDateFont = allFonts.find(
        (f) =>
          f.fontName?.toLowerCase() === "barlow bold" ||
          f.fontName?.toLowerCase() === "barlowbold"
      );
      if (defaultDateFont) {
        document.fonts.load(`16px "${defaultDateFont.fontName}"`).finally(() => {
          setClockSettings((prev) => {
            if (prev.dateFontId == null) {
              return { ...prev, dateFontFamily: defaultDateFont.fontName, dateFontId: defaultDateFont.id };
            }
            return prev;
          });
        });
      }
    }).catch(() => { });
  }, []);

  // Resolve font names from ids once API fonts are loaded
  useEffect(() => {
    if (apiFontCategories.length === 0) return;
    const allFonts = apiFontCategories.flatMap((c) => c.fonts);
    setClockSettings((prev) => {
      const updates = {};
      if (prev.clockFontId != null) {
        const found = allFonts.find((f) => f.id === prev.clockFontId || f.id === String(prev.clockFontId));
        if (found && prev.clockFontFamily !== found.value) {
          updates.clockFontFamily = found.value;
        }
      }
      if (prev.dateFontId != null) {
        const found = allFonts.find((f) => f.id === prev.dateFontId || f.id === String(prev.dateFontId));
        if (found && prev.dateFontFamily !== found.value) {
          updates.dateFontFamily = found.value;
        }
      }
      return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
    });
  }, [
    apiFontCategories,
    clockSettings.clockFontId,
    clockSettings.dateFontId,
    clockSettings.clockFontFamily,
    clockSettings.dateFontFamily,
  ]);

  const { loader, handleClickOpen: setLoader, handleClose: unSetLoader } = useDialogState();

  // ── File upload handlers ──────────────────────────────────────────────────
  const handleBgChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (img.width === 1080 && img.height === 2400) {
        setBgFile(file);
        setBgError("");
        setBgFileName(file.name);
        setBgImage(url);
      } else {
        URL.revokeObjectURL(url);
        e.target.value = "";
        setBgFile(null);
        setBgImage(null);
        setBgFileName("");
        setBgError(`Invalid size (${img.width}×${img.height}). Only 1080×2400 (9:20) images are allowed.`);
      }
    };
    img.src = url;
  };

  const handleFgChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (img.width === 1080 && img.height === 2400) {
        setFgFile(file);
        setFgError("");
        setFgPreviewUrl(url);
      } else {
        URL.revokeObjectURL(url);
        e.target.value = "";
        setFgFile(null);
        setFgPreviewUrl(null);
        setFgError(`Invalid size (${img.width}×${img.height}). Only 1080×2400 (9:20) images are allowed.`);
      }
    };
    img.src = url;
  };


  // ── Tag helpers ───────────────────────────────────────────────────────────
  const handleAddTag = () => {
    if (tagInput.trim()) {
      setTags((prev) => [...prev, tagInput.trim()]);
      setTagInput("");
    }
  };
  const handleRemoveTag = (tag) => setTags((prev) => prev.filter((t) => t !== tag));

  // ── Clock setting helper ──────────────────────────────────────────────────
  const setClock = (key, val) => {
    setClockSettings((prev) => {
      const next = { ...prev, [key]: val };
      if (key === "previewTime") next.preveiwTime = val;
      if (key === "preveiwTime") next.previewTime = val;
      if (key === "previewDate") next.preveiwDate = val;
      if (key === "preveiwDate") next.previewDate = val;
      if (key === "showSeconds") {
        const curTime = prev.previewTime || prev.preveiwTime || (prev.is24Hr ? "20:45" : "08:45");
        const parts = curTime.split(":");
        if (val) {
          const newTime = `${parts[0] || "08"}:${parts[1] || "45"}:${parts[2] || "00"}`;
          next.previewTime = newTime;
          next.preveiwTime = newTime;
        } else {
          const newTime = `${parts[0] || "08"}:${parts[1] || "45"}`;
          next.previewTime = newTime;
          next.preveiwTime = newTime;
          const currentTargets = Array.isArray(prev.clockBorderTarget) ? prev.clockBorderTarget : ["Full"];
          const filtered = currentTargets.filter((t) => t !== "Seconds");
          if (filtered.includes("Hours") && filtered.includes("Minutes")) {
            next.clockBorderTarget = ["Full"];
          } else {
            next.clockBorderTarget = filtered.length > 0 ? filtered : ["Full"];
          }
        }
      }
      return next;
    });
    if (key === "previewTime" || key === "preveiwTime") {
      try { localStorage.setItem("admin_default_clock_time", val); } catch {}
    } else if (key === "previewDate" || key === "preveiwDate") {
      try { localStorage.setItem("admin_default_clock_date", val); } catch {}
    }
  };

  const toggleBorderTarget = (target) => {
    setClockSettings((prev) => {
      const current = Array.isArray(prev.clockBorderTarget) && prev.clockBorderTarget.length > 0
        ? prev.clockBorderTarget
        : ["Full"];

      if (target === "Full") {
        return { ...prev, clockBorderTarget: ["Full"] };
      }

      let nextList = current.filter((t) => t !== "Full");
      if (nextList.includes(target)) {
        nextList = nextList.filter((t) => t !== target);
        if (nextList.length === 0) {
          nextList = ["Full"];
        }
      } else {
        nextList = [...nextList, target];
      }

      // When all available targets are selected, automatically default to "Full"
      const availableTargets = prev.showSeconds
        ? ["Hours", "Minutes", "Seconds"]
        : ["Hours", "Minutes"];

      if (availableTargets.every((t) => nextList.includes(t))) {
        nextList = ["Full"];
      }

      return { ...prev, clockBorderTarget: nextList };
    });
  };

  const getFormattedPreviewDate = () => {
    let targetDate = new Date();
    const dateVal = clockSettings.previewDate || clockSettings.preveiwDate;
    if (dateVal) {
      const parts = dateVal.split("-").map(Number);
      if (parts.length === 3 && !parts.some(isNaN)) {
        targetDate = new Date(parts[0], parts[1] - 1, parts[2]);
      }
    }
    return buildDateStr("Day Date Month", targetDate);
  };

  const resetAllClockSettings = () => {
    const allFonts = apiFontCategories.flatMap((category) => category.fonts);
    const savedSettings = initialClockSettings.current;
    const restored = { ...(savedSettings || DEFAULT_CLOCK) };

    // API clock settings store font IDs. Resolve those IDs back to the family
    // names required by the browser canvas instead of retaining a font chosen
    // after the record was opened.
    const resolveFont = (fontId) => allFonts.find(
      (font) => String(font.id) === String(fontId)
    );

    if (savedSettings) {
      const clockFont = resolveFont(restored.clockFontId);
      const dateFont = resolveFont(restored.dateFontId);
      if (clockFont) restored.clockFontFamily = clockFont.value;
      if (dateFont) restored.dateFontFamily = dateFont.value;
    } else {
      // New records reset to the same default font used when this page opens.
      const defaultFont = allFonts.find((font) => font.value === "Calcio") || allFonts[0];
      if (defaultFont) {
        restored.clockFontFamily = defaultFont.value;
        restored.clockFontId = defaultFont.id;
      }
      restored.previewTime = getDefaultPreviewTime();
      restored.preveiwTime = getDefaultPreviewTime();
      restored.previewDate = getDefaultPreviewDate();
      restored.preveiwDate = getDefaultPreviewDate();
    }

    setClockSettings(restored);
  };

  // ── Direction arrows ──────────────────────────────────────────────────────
  const STEP = 5;
  const moveUp = () => {
    const key = activeTab === "date" ? "dateVPos" : "clockVPos";
    setClock(key, Math.max(0, clockSettings[key] - STEP));
  };
  const moveDown = () => {
    const key = activeTab === "date" ? "dateVPos" : "clockVPos";
    setClock(key, Math.min(100, clockSettings[key] + STEP));
  };
  const moveLeft = () => {
    const key = activeTab === "date" ? "dateHPos" : "clockHPos";
    setClock(key, Math.max(0, clockSettings[key] - STEP));
  };
  const moveRight = () => {
    const key = activeTab === "date" ? "dateHPos" : "clockHPos";
    setClock(key, Math.min(100, clockSettings[key] + STEP));
  };

  // ── Always reset clock settings when component mounts ────────────────────
  useEffect(() => {
    setClockSettings({
      ...DEFAULT_CLOCK,
      previewTime: getDefaultPreviewTime(),
      preveiwTime: getDefaultPreviewTime(),
      previewDate: getDefaultPreviewDate(),
      preveiwDate: getDefaultPreviewDate(),
    });
  }, []);

  const fetchCategories = async () => {
    try {
      const r = await CategoryService.GetAllCategory(0, 0);
      const cats =
        r?.data?.apiresponse?.data?.categories ||
        r?.data?.data?.categories ||
        r?.data?.categories ||
        [];
      if (Array.isArray(cats)) setCategoryData(cats);
    } catch (e) {
      console.error("category fetch error", e);
    }
  };

  const fetchCollections = async () => {
    try {
      const r = await CollectionService.GetAllCollection(0, 0);
      const cols =
        r?.data?.apiresponse?.data?.collections ||
        r?.data?.data?.collections ||
        r?.data?.collections ||
        [];
      if (Array.isArray(cols)) setCollectionData(cols);
    } catch (e) {
      console.error("collection fetch error", e);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchCollections();
  }, []);

  const CategoryOption = (categoryData || []).map((item) => ({
    label: item.categoryName,
    value: item.id,
  }));

  const CollectionOption = (collectionData || []).map((item) => ({
    label: item.collectionName,
    value: item.id,
  }));

  const ImageType = [
    { value: "PREMIUM", label: "PREMIUM" },
    { value: "NON PREMIUM", label: "NON PREMIUM" },
  ];

  // ── Formik ────────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: {
      imageId: "",
      imageName: "",
      premium: false,
      categoryId: "",
      collectionId: "",
      tags: [],
    },
    validationSchema: yup.object().shape({
      imageName: yup.string().required("Please enter image name"),
    }),
    onSubmit: async (values, action) => {
      const isEditing = !!values.imageId;
      if (!isEditing && !bgFile) {
        setBgError("Please upload background image");
        return;
      }
      if (!isEditing && !fgFile) {
        setFgError("Please upload foreground image");
        return;
      }
      setBgError("");
      setFgError("");
      try {
        setLoader();

        let previewImageFile = null;
        try {
          if (wallpaperPreviewRef.current) {
            const blob = await wallpaperPreviewRef.current.exportBlob();
            if (blob) previewImageFile = new File([blob], "preview.png", { type: "image/png" });
          }
        } catch (e) {
          console.warn("Preview generation failed", e);
        }

        const builtClockSettings = {
          is24Hr: clockSettings.is24Hr,
          is12Hr: !clockSettings.is24Hr,
          showSeconds: clockSettings.showSeconds,
          showMinutes: clockSettings.showMinutes,
          showHours: clockSettings.showHours,
          showClockHorizontal: clockSettings.showClockHorizontal,
          showColon: clockSettings.showColon,
          hoursForward: clockSettings.hoursForward,
          minutesForward: clockSettings.minutesForward,
          secondsForward: clockSettings.secondsForward,
          clockFontFamily: clockSettings.clockFontId != null ? parseInt(clockSettings.clockFontId, 10) : null,
          clockFontSize: clockSettings.clockFontSize,
          clockOpacity: clockSettings.clockOpacity,
          clockHPos: clockSettings.clockHPos,
          clockVPos: clockSettings.clockVPos,
          clockColor: clockSettings.clockColor,
          clockColor2: clockSettings.clockColor2,
          clockRotation: clockSettings.clockRotation,
          perspRotX: clockSettings.perspRotX,
          perspRotY: clockSettings.perspRotY,
          perspRotZ: clockSettings.perspRotZ,
          hStretch: clockSettings.hStretch,
          vStretch: clockSettings.vStretch,
          hSkew: clockSettings.hSkew,
          vSkew: clockSettings.vSkew,
          gradientEnabled: clockSettings.gradientEnabled,
          gradientType: clockSettings.gradientEnabled ? clockSettings.gradientType : "linear",
          gradAngle: clockSettings.gradientEnabled ? clockSettings.gradAngle : 180,
          gradStop1: clockSettings.gradientEnabled ? clockSettings.gradStop1 : 0.90,
          gradStop2: clockSettings.gradientEnabled ? clockSettings.gradStop2 : 0.40,
          dateEnabled: clockSettings.dateEnabled,
          dateFontFamily: clockSettings.dateEnabled ? (clockSettings.dateFontId != null ? parseInt(clockSettings.dateFontId, 10) : 36) : (clockSettings.clockFontId != null ? parseInt(clockSettings.clockFontId, 10) : 36),
          dateFontSize: clockSettings.dateEnabled ? clockSettings.dateFontSize : 20,
          dateOpacity: clockSettings.dateEnabled ? clockSettings.dateOpacity : 100,
          dateHPos: clockSettings.dateEnabled ? clockSettings.dateHPos : 50,
          dateVPos: clockSettings.dateEnabled ? clockSettings.dateVPos : 20,
          dateColor: clockSettings.dateEnabled ? clockSettings.dateColor : "#ffffff",
          dateRotation: clockSettings.dateEnabled ? clockSettings.dateRotation : 0,
          dateFormat: clockSettings.dateEnabled ? clockSettings.dateFormat : "Day Date Month",
          borderEnabled: clockSettings.borderEnabled,
          fillBorderEnabled: clockSettings.borderEnabled ? clockSettings.fillBorderEnabled : true,
          clockBorderTarget: (() => {
            const targets = Array.isArray(clockSettings.clockBorderTarget) && clockSettings.clockBorderTarget.length > 0
              ? clockSettings.clockBorderTarget
              : ["Full"];
            const sanitized = !clockSettings.showSeconds
              ? targets.filter((t) => t !== "Seconds")
              : targets;
            const available = clockSettings.showSeconds
              ? ["Hours", "Minutes", "Seconds"]
              : ["Hours", "Minutes"];
            if (available.every((t) => sanitized.includes(t))) {
              return ["Full"];
            }
            return sanitized.length > 0 ? sanitized : ["Full"];
          })(),
          borderColor: clockSettings.borderEnabled ? clockSettings.borderColor : "#ffffff",
          borderWidth: clockSettings.borderEnabled ? clockSettings.borderWidth : 1.0,
          borderOpacity: clockSettings.borderEnabled ? clockSettings.borderOpacity : 1.0,
          shadowEnabled: clockSettings.shadowEnabled,
          shadowColor: clockSettings.shadowEnabled ? clockSettings.shadowColor : "#ffffff",
          shadowHOffset: clockSettings.shadowEnabled ? clockSettings.shadowHOffset : 0.0,
          shadowVOffset: clockSettings.shadowEnabled ? clockSettings.shadowVOffset : 0.0,
          shadowBlur: clockSettings.shadowEnabled ? clockSettings.shadowBlur : 1.0,
          shadowOpacity: clockSettings.shadowEnabled ? clockSettings.shadowOpacity : 0.5,
          clockLineSpacing: clockSettings.clockLineSpacing ?? 0,
          clockLetterSpacing: clockSettings.clockLetterSpacing ?? 0,
          previewTime: clockSettings.previewTime,
          preveiwTime: clockSettings.preveiwTime || clockSettings.previewTime,
          previewDate: clockSettings.previewDate,
          preveiwDate: clockSettings.preveiwDate || clockSettings.previewDate,
        };

        const formData = new FormData();
        if (values.imageId) formData.append("id", values.imageId);
        formData.append("imageName", values.imageName);
        formData.append("premium", values.premium);
        formData.append("categoryId", values.categoryId || 0);
        formData.append("collectionId", values.collectionId || 0);
        formData.append("tags", tags.join(","));
        formData.append("sendNotification", notification);
        if (bgFile) formData.append("backgroundImage", bgFile);
        if (fgFile) formData.append("foregroundImage", fgFile);
        formData.append("clockSettings", JSON.stringify(builtClockSettings));
        formData.append("showHour", clockSettings.showHours);
        if (previewImageFile) formData.append("previewImage", previewImageFile);

        if (values.imageId) {
          await LiveClockService.updateClockWallpaper(values.imageId, formData);
        } else {
          await LiveClockService.createClockWallpaper(formData);
        }

        notifySuccess(
          values.imageId
            ? "Live clock wallpaper updated successfully"
            : "Live clock wallpaper created successfully"
        );
        setTimeout(() => {
          unSetLoader();
          navigate("/images");
          action.resetForm();
          setClockSettings({
            ...DEFAULT_CLOCK,
            previewTime: getDefaultPreviewTime(),
            preveiwTime: getDefaultPreviewTime(),
            previewDate: getDefaultPreviewDate(),
            preveiwDate: getDefaultPreviewDate(),
          });
        }, 1500);
      } catch (error) {
        console.error("error", error);
        notifyError("Something went wrong");
        setTimeout(() => {
          unSetLoader();
          navigate(-1);
          action.resetForm();
        }, 1500);
      }
    },
  });

  const resetQuickSettingFromApi = async (key) => {
    const recordId = formik.values.imageId || editRecordId;

    // New wallpapers have no server state yet, so only they use local defaults.
    if (!recordId) {
      setClock(key, DEFAULT_CLOCK[key]);
      return;
    }

    try {
      const res = await LiveClockService.getClockWallpaper(recordId);
      const full = res?.data?.apiresponse?.data || res?.data?.data || res?.data || {};
      let saved = full.clockSettings || {};
      if (typeof saved === "string") saved = JSON.parse(saved);

      const resetValue = saved[key] !== undefined ? saved[key] : DEFAULT_CLOCK[key];
      setClock(key, resetValue);
      initialClockSettings.current = {
        ...(initialClockSettings.current || DEFAULT_CLOCK),
        [key]: resetValue,
      };
    } catch (error) {
      console.error(`Failed to reset ${key} from API`, error);
      notifyError("Unable to restore the saved value");
    }
  };

  // ── Navigation & Edit mode: fetch record on route / state change ─────────────
  useEffect(() => {
    const navKey = location.key || location.search || "root";
    if (lastLoadedKey.current === navKey) return;
    lastLoadedKey.current = navKey;

    if (!categoryData || categoryData.length === 0) fetchCategories();
    if (!collectionData || collectionData.length === 0) fetchCollections();

    const data = location.state || {};
    const recordId = data.imageId || data.id || queryEditId;

    if (!recordId) {
      // Add mode: reset form & state
      formik.resetForm();
      setBgImage(null);
      setBgFileName("");
      setBgFile(null);
      setFgPreviewUrl(null);
      setFgFile(null);
      setShowVal("NON PREMIUM");
      setShowVal2("");
      setShowVal3("");
      setSelectedCatId(null);
      setSelectedColId(null);
      setTags([]);
      setClockSettings({
        ...DEFAULT_CLOCK,
        previewTime: getDefaultPreviewTime(),
        preveiwTime: getDefaultPreviewTime(),
        previewDate: getDefaultPreviewDate(),
        preveiwDate: getDefaultPreviewDate(),
      });
      setActivePage(0);
      setActiveTab("clock");
      return;
    }

    formik.setFieldValue("imageId", recordId);
    if (data.imageName !== undefined) {
      formik.setFieldValue("imageName", data.imageName || "");
    }
    const statePremium = data.isPremium ?? data.premium;
    if (statePremium !== undefined) {
      const isPremium = statePremium === true || statePremium === "true";
      formik.setFieldValue("premium", isPremium);
      setShowVal(isPremium ? "PREMIUM" : "NON PREMIUM");
    }
    if (data.tags !== undefined) {
      setTags(sanitizeTags(data.tags));
    }
    if (data.categoryId) {
      setSelectedCatId(data.categoryId);
      formik.setFieldValue("categoryId", data.categoryId);
    }
    if (data.categoryName) {
      setShowVal2(data.categoryName);
    }
    if (data.collectionId) {
      setSelectedColId(data.collectionId);
      formik.setFieldValue("collectionId", data.collectionId);
    }
    if (data.collectionName) {
      setShowVal3(data.collectionName);
    }

    // Pre-fill preview immediately from location.state so the preview shows
    // without waiting for the API — the API call below will override with full URLs.
    if (data.backgroundImage) {
      setBgImage(data.backgroundImage);
      setBgFileName(data.backgroundImage.split("/").pop() || "background-image");
    } else if (data.url) {
      setBgImage(data.url);
      setBgFileName(data.url.split("/").pop() || "background-image");
    }
    if (data.foregroundImage) setFgPreviewUrl(data.foregroundImage);

    // Apply clockSettings immediately from location.state if present
    const stateSettings = data.clockSettings;
    if (stateSettings) {
      const saved = typeof stateSettings === "string" ? (() => { try { return JSON.parse(stateSettings); } catch { return {}; } })() : stateSettings;
      if (Object.keys(saved).length > 0) {
        setClockSettings((prev) => {
          const newState = {
            ...prev,
            ...(saved.is24Hr !== undefined && { is24Hr: saved.is24Hr }),
            ...(saved.showSeconds !== undefined && { showSeconds: saved.showSeconds }),
            ...(saved.showMinutes !== undefined && { showMinutes: saved.showMinutes }),
            ...((data.showHour !== undefined ? { showHours: data.showHour } : (saved.showHours !== undefined && { showHours: saved.showHours }))),
            ...(saved.hoursForward !== undefined && { hoursForward: saved.hoursForward }),
            ...(saved.minutesForward !== undefined && { minutesForward: saved.minutesForward }),
            ...(saved.secondsForward !== undefined && { secondsForward: saved.secondsForward }),
            ...(saved.showClockHorizontal !== undefined && { showClockHorizontal: saved.showClockHorizontal }),
            ...(saved.showColon !== undefined && { showColon: saved.showColon }),
            ...(saved.clockFontFamily != null && saved.clockFontFamily !== "monospace" && { clockFontId: parseInt(saved.clockFontFamily, 10), clockFontFamily: "" }),
            ...(saved.clockFontSize !== undefined && { clockFontSize: saved.clockFontSize }),
            ...(saved.clockOpacity !== undefined && { clockOpacity: saved.clockOpacity }),
            ...(saved.clockHPos !== undefined && { clockHPos: saved.clockHPos }),
            ...(saved.clockVPos !== undefined && { clockVPos: saved.clockVPos }),
            ...(saved.clockColor && { clockColor: saved.clockColor }),
            ...(saved.clockColor2 && { clockColor2: saved.clockColor2 }),
            ...(saved.clockRotation !== undefined && { clockRotation: saved.clockRotation }),
            ...(saved.dateEnabled !== undefined && { dateEnabled: saved.dateEnabled }),
            ...(saved.dateFontFamily != null && saved.dateFontFamily !== "monospace" && { dateFontId: parseInt(saved.dateFontFamily, 10), dateFontFamily: "" }),
            ...(saved.dateFontSize !== undefined && { dateFontSize: saved.dateFontSize }),
            ...(saved.dateOpacity !== undefined && { dateOpacity: saved.dateOpacity }),
            ...(saved.dateHPos !== undefined && { dateHPos: saved.dateHPos }),
            ...(saved.dateVPos !== undefined && { dateVPos: saved.dateVPos }),
            ...(saved.dateColor && { dateColor: saved.dateColor }),
            ...(saved.dateFormat && { dateFormat: saved.dateFormat }),
            ...(saved.dateRotation !== undefined && { dateRotation: saved.dateRotation }),
            ...(saved.gradientEnabled !== undefined && { gradientEnabled: saved.gradientEnabled }),
            ...(saved.gradientType && { gradientType: saved.gradientType }),
            ...(saved.gradAngle !== undefined && { gradAngle: saved.gradAngle }),
            ...(saved.gradStop1 !== undefined && { gradStop1: saved.gradStop1 }),
            ...(saved.gradStop2 !== undefined && { gradStop2: saved.gradStop2 }),
            ...(saved.perspRotX !== undefined && { perspRotX: saved.perspRotX }),
            ...(saved.perspRotY !== undefined && { perspRotY: saved.perspRotY }),
            ...(saved.perspRotZ !== undefined && { perspRotZ: saved.perspRotZ }),
            ...(saved.hStretch !== undefined && { hStretch: saved.hStretch }),
            ...(saved.vStretch !== undefined && { vStretch: saved.vStretch }),
            ...(saved.hSkew !== undefined && { hSkew: saved.hSkew }),
            ...(saved.vSkew !== undefined && { vSkew: saved.vSkew }),
            ...(saved.borderEnabled !== undefined && { borderEnabled: saved.borderEnabled }),
            ...(saved.fillBorderEnabled !== undefined && { fillBorderEnabled: saved.fillBorderEnabled }),
            ...(saved.clockBorderTarget && {
              clockBorderTarget: (() => {
                let val = saved.clockBorderTarget;
                if (typeof val === "string") {
                  try { val = JSON.parse(val); } catch (_) { val = [val]; }
                }
                return Array.isArray(val) && val.length > 0 ? val : ["Full"];
              })(),
            }),
            ...(saved.borderColor && { borderColor: saved.borderColor }),
            ...(saved.borderWidth !== undefined && { borderWidth: saved.borderWidth }),
            ...(saved.borderOpacity !== undefined && { borderOpacity: saved.borderOpacity }),
            ...(saved.shadowEnabled !== undefined && { shadowEnabled: saved.shadowEnabled }),
            ...(saved.shadowColor && { shadowColor: saved.shadowColor }),
            ...(saved.shadowHOffset !== undefined && { shadowHOffset: saved.shadowHOffset }),
            ...(saved.shadowVOffset !== undefined && { shadowVOffset: saved.shadowVOffset }),
            ...(saved.shadowBlur !== undefined && { shadowBlur: saved.shadowBlur }),
            ...(saved.shadowOpacity !== undefined && { shadowOpacity: saved.shadowOpacity }),
            ...(saved.clockLineSpacing !== undefined && { clockLineSpacing: saved.clockLineSpacing }),
            ...(saved.clockLetterSpacing !== undefined && { clockLetterSpacing: saved.clockLetterSpacing }),
            previewTime: (() => {
              const raw = saved.preveiwTime || saved.previewTime || (saved.is24Hr ? "20:45" : "08:45");
              const parts = raw.split(":");
              if (saved.showSeconds && parts.length < 3) return `${parts[0] || "08"}:${parts[1] || "45"}:00`;
              if (!saved.showSeconds && parts.length >= 3) return `${parts[0] || "08"}:${parts[1] || "45"}`;
              return raw;
            })(),
            preveiwTime: (() => {
              const raw = saved.preveiwTime || saved.previewTime || (saved.is24Hr ? "20:45" : "08:45");
              const parts = raw.split(":");
              if (saved.showSeconds && parts.length < 3) return `${parts[0] || "08"}:${parts[1] || "45"}:00`;
              if (!saved.showSeconds && parts.length >= 3) return `${parts[0] || "08"}:${parts[1] || "45"}`;
              return raw;
            })(),
            previewDate: saved.previewDate || saved.preveiwDate || getDefaultPreviewDate(),
            preveiwDate: saved.previewDate || saved.preveiwDate || getDefaultPreviewDate(),
          };
          initialClockSettings.current = newState;
          return newState;
        });
        if (saved.backgroundImage) {
          setBgImage(saved.backgroundImage);
          setBgFileName(saved.backgroundImage.split("/").pop() || "background-image");
        }
        if (saved.foregroundImage) setFgPreviewUrl(saved.foregroundImage);
      }
    }

    LiveClockService.getClockWallpaper(recordId)
      .then((res) => {
        const full = res?.data?.apiresponse?.data || res?.data?.data || res?.data || {};

        let saved = {};
        if (full.clockSettings) {
          try {
            saved = typeof full.clockSettings === "string"
              ? JSON.parse(full.clockSettings)
              : full.clockSettings;
          } catch { saved = {}; }
        }

        const rawBg = saved.backgroundImage || full.backgroundImage || full.url;
        if (rawBg) {
          setBgImage(rawBg);
          setBgFileName(rawBg.split("/").pop() || "background-image");
        }
        const rawFg = saved.foregroundImage || full.foregroundImage;
        if (rawFg) setFgPreviewUrl(rawFg);

        formik.setFieldValue("imageId", recordId);
        if (full.imageName !== undefined) {
          formik.setFieldValue("imageName", full.imageName || "");
        }
        const apiPremium = full.isPremium ?? full.premium;
        if (apiPremium !== undefined) {
          const isPremium = apiPremium === true || apiPremium === "true";
          formik.setFieldValue("premium", isPremium);
          setShowVal(isPremium ? "PREMIUM" : "NON PREMIUM");
        }
        if (full.tags !== undefined) {
          setTags(sanitizeTags(full.tags));
        }

        const resolvedCatId = full.categoryId || data.categoryId;
        if (resolvedCatId) {
          setSelectedCatId(resolvedCatId);
          formik.setFieldValue("categoryId", resolvedCatId);
        }
        if (full.categoryName) {
          setShowVal2(full.categoryName);
        }

        const resolvedColId = full.collectionId || data.collectionId;
        if (resolvedColId) {
          setSelectedColId(resolvedColId);
          formik.setFieldValue("collectionId", resolvedColId);
        }
        if (full.collectionName) {
          setShowVal3(full.collectionName);
        }

        if (Object.keys(saved).length > 0) {
          setClockSettings((prev) => {
            const newState = {
              ...prev,
              ...(saved.is24Hr !== undefined && { is24Hr: saved.is24Hr }),
              ...(saved.showSeconds !== undefined && { showSeconds: saved.showSeconds }),
              ...(saved.showMinutes !== undefined && { showMinutes: saved.showMinutes }),
              ...((full.showHour !== undefined ? { showHours: full.showHour } : (saved.showHours !== undefined && { showHours: saved.showHours }))),
              ...(saved.hoursForward !== undefined && { hoursForward: saved.hoursForward }),
              ...(saved.minutesForward !== undefined && { minutesForward: saved.minutesForward }),
              ...(saved.secondsForward !== undefined && { secondsForward: saved.secondsForward }),
              ...(saved.showClockHorizontal !== undefined && { showClockHorizontal: saved.showClockHorizontal }),
              ...(saved.showColon !== undefined && { showColon: saved.showColon }),
              ...(saved.clockFontFamily != null && saved.clockFontFamily !== "monospace" && { clockFontId: parseInt(saved.clockFontFamily, 10), clockFontFamily: "" }),
              ...(saved.clockFontSize !== undefined && { clockFontSize: saved.clockFontSize }),
              ...(saved.clockOpacity !== undefined && { clockOpacity: saved.clockOpacity }),
              ...(saved.clockHPos !== undefined && { clockHPos: saved.clockHPos }),
              ...(saved.clockVPos !== undefined && { clockVPos: saved.clockVPos }),
              ...(saved.clockColor && { clockColor: saved.clockColor }),
              ...(saved.clockColor2 && { clockColor2: saved.clockColor2 }),
              ...(saved.clockRotation !== undefined && { clockRotation: saved.clockRotation }),
              ...(saved.dateEnabled !== undefined && { dateEnabled: saved.dateEnabled }),
              ...(saved.dateFontFamily != null && saved.dateFontFamily !== "monospace" && { dateFontId: parseInt(saved.dateFontFamily, 10), dateFontFamily: "" }),
              ...(saved.dateFontSize !== undefined && { dateFontSize: saved.dateFontSize }),
              ...(saved.dateOpacity !== undefined && { dateOpacity: saved.dateOpacity }),
              ...(saved.dateHPos !== undefined && { dateHPos: saved.dateHPos }),
              ...(saved.dateVPos !== undefined && { dateVPos: saved.dateVPos }),
              ...(saved.dateColor && { dateColor: saved.dateColor }),
              ...(saved.dateFormat && { dateFormat: saved.dateFormat }),
              ...(saved.dateRotation !== undefined && { dateRotation: saved.dateRotation }),
              ...(saved.gradientEnabled !== undefined && { gradientEnabled: saved.gradientEnabled }),
              ...(saved.gradientType && { gradientType: saved.gradientType }),
              ...(saved.gradAngle !== undefined && { gradAngle: saved.gradAngle }),
              ...(saved.gradStop1 !== undefined && { gradStop1: saved.gradStop1 }),
              ...(saved.gradStop2 !== undefined && { gradStop2: saved.gradStop2 }),
              ...(saved.perspRotX !== undefined && { perspRotX: saved.perspRotX }),
              ...(saved.perspRotY !== undefined && { perspRotY: saved.perspRotY }),
              ...(saved.perspRotZ !== undefined && { perspRotZ: saved.perspRotZ }),
              ...(saved.hStretch !== undefined && { hStretch: saved.hStretch }),
              ...(saved.vStretch !== undefined && { vStretch: saved.vStretch }),
              ...(saved.hSkew !== undefined && { hSkew: saved.hSkew }),
              ...(saved.vSkew !== undefined && { vSkew: saved.vSkew }),
              ...(saved.borderEnabled !== undefined && { borderEnabled: saved.borderEnabled }),
              ...(saved.fillBorderEnabled !== undefined && { fillBorderEnabled: saved.fillBorderEnabled }),
              ...(saved.clockBorderTarget && {
                clockBorderTarget: (() => {
                  let val = saved.clockBorderTarget;
                  if (typeof val === "string") {
                    try { val = JSON.parse(val); } catch (_) { val = [val]; }
                  }
                  return Array.isArray(val) && val.length > 0 ? val : ["Full"];
                })(),
              }),
              ...(saved.borderColor && { borderColor: saved.borderColor }),
              ...(saved.borderWidth !== undefined && { borderWidth: saved.borderWidth }),
              ...(saved.borderOpacity !== undefined && { borderOpacity: saved.borderOpacity }),
              ...(saved.shadowEnabled !== undefined && { shadowEnabled: saved.shadowEnabled }),
              ...(saved.shadowColor && { shadowColor: saved.shadowColor }),
              ...(saved.shadowHOffset !== undefined && { shadowHOffset: saved.shadowHOffset }),
              ...(saved.shadowVOffset !== undefined && { shadowVOffset: saved.shadowVOffset }),
              ...(saved.shadowBlur !== undefined && { shadowBlur: saved.shadowBlur }),
              ...(saved.shadowOpacity !== undefined && { shadowOpacity: saved.shadowOpacity }),
              ...(saved.clockLineSpacing !== undefined && { clockLineSpacing: saved.clockLineSpacing }),
              ...(saved.clockLetterSpacing !== undefined && { clockLetterSpacing: saved.clockLetterSpacing }),
              previewTime: (() => {
                const raw = saved.preveiwTime || saved.previewTime || (saved.is24Hr ? "20:45" : "08:45");
                const parts = raw.split(":");
                if (saved.showSeconds && parts.length < 3) return `${parts[0] || "08"}:${parts[1] || "45"}:00`;
                if (!saved.showSeconds && parts.length >= 3) return `${parts[0] || "08"}:${parts[1] || "45"}`;
                return raw;
              })(),
              preveiwTime: (() => {
                const raw = saved.preveiwTime || saved.previewTime || (saved.is24Hr ? "20:45" : "08:45");
                const parts = raw.split(":");
                if (saved.showSeconds && parts.length < 3) return `${parts[0] || "08"}:${parts[1] || "45"}:00`;
                if (!saved.showSeconds && parts.length >= 3) return `${parts[0] || "08"}:${parts[1] || "45"}`;
                return raw;
              })(),
              previewDate: saved.previewDate || saved.preveiwDate || getDefaultPreviewDate(),
              preveiwDate: saved.previewDate || saved.preveiwDate || getDefaultPreviewDate(),
            };
            initialClockSettings.current = newState;
            return newState;
          });
          if (saved.activeTab) setActiveTab(saved.activeTab);
        }
      })
      .catch((err) => {
        console.error("Failed to load clock wallpaper record", err);
      });
  }, [location.key, location.search, location.state]);

  // ── Sync category label from categoryData when ID or data changes ───────────
  useEffect(() => {
    const catId = selectedCatId || formik.values.categoryId;
    if (catId && categoryData && categoryData.length > 0) {
      const cat = categoryData.find((c) => String(c.id) === String(catId));
      if (cat) {
        setShowVal2(cat.categoryName);
        if (!formik.values.categoryId) {
          formik.setFieldValue("categoryId", cat.id);
        }
      }
    }
  }, [selectedCatId, categoryData, formik.values.categoryId]);

  // ── Sync collection label from collectionData when ID or data changes ───────
  useEffect(() => {
    const colId = selectedColId || formik.values.collectionId;
    if (colId && collectionData && collectionData.length > 0) {
      const col = collectionData.find((c) => String(c.id) === String(colId));
      if (col) {
        setShowVal3(col.collectionName);
        if (!formik.values.collectionId) {
          formik.setFieldValue("collectionId", col.id);
        }
      }
    }
  }, [selectedColId, collectionData, formik.values.collectionId]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="max-w-[1100px] min-w-[300px] w-auto px-5 pb-10" ref={menuRef}>
        <form onSubmit={formik.handleSubmit}>
          <div className="pt-10 pb-5">
            <h1 className="text-base font-semibold leading-7 text-white">
              Live Clock
            </h1>
          </div>

          {/* ── Top: Tags button ── */}
          <div className="pb-4">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="btn save w-40"
            >
              Add Tags
            </button>

            {/* Tag modal */}
            {open && (
              <div className="fixed inset-0 bg-black/60 z-[999] overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen px-4">
                  <div className="bg-white overflow-hidden rounded-6 p-5 lg:p-7 max-w-[500px] w-full min-w-[350px]">
                    <h1 className="font-medium mb-5">Adding Title</h1>
                    <div className="flex flex-col gap-2 mb-7">
                      <label className="text-sm font-medium">Tag Title</label>
                      <input
                        className="bg-white w-full py-1.5 px-3 rounded-6 focus:outline-none border border-blue"
                        type="text"
                        placeholder="Enter Tag Name..."
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                      />
                      <button
                        className="bg-blue w-full p-2 rounded-6 text-white font-medium"
                        onClick={handleAddTag}
                        type="button"
                      >
                        Add Tag
                      </button>
                    </div>
                    {tags?.length > 0 &&
                      tags.map((tag, i) => (
                        <div key={i} className="border rounded-6 mb-1">
                          <div className="grid grid-cols-4 items-center border-b px-3 py-1.5">
                            <div className="col-span-3">
                              <span className="text-sm">{tag}</span>
                            </div>
                            <div className="col-span-1 flex justify-end border-l">
                              <button
                                className="bg-red/80 text-white text-sm font-medium rounded-6 py-1.5 px-2.5"
                                type="button"
                                onClick={() => handleRemoveTag(tag)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    <div className="flex justify-end gap-4 items-center mt-7">
                      <button
                        onClick={() => {
                          setOpen(false);
                          setTags([]);
                          setTagInput("");
                        }}
                        type="button"
                        className="border border-red/80 text-red/80 font-medium rounded-6 py-1.5 px-2.5 w-full"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => setOpen(false)}
                        type="button"
                        className="bg-blue text-white font-medium rounded-6 py-1.5 px-2.5 w-full"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tag chips */}
            <div className="my-3">
              {!open &&
                tags?.length > 0 &&
                tags.map((tag, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-white py-1.5 px-3 rounded-full border-blue border mb-1"
                  >
                    {tag}
                    <MdDeleteOutline
                      onClick={() => handleRemoveTag(tag)}
                      className="text-red text-2xl cursor-pointer"
                    />
                  </div>
                ))}
            </div>
          </div>

          {/* ── Two-column layout ── */}
          <div className="flex flex-col lg:flex-row gap-6 items-start">

            {/* LEFT: Image Name + Image Type + Background + Preview */}
            <div className="w-[45%] flex flex-col gap-4">

              {/* Image Name */}
              <div className="flex flex-col gap-2">
                <label className="label">Image Name</label>
                <input
                  className="input"
                  name="imageName"
                  placeholder="image name"
                  value={formik.values.imageName}
                  onChange={formik.handleChange}
                  type="text"
                />
                {formik.errors.imageName && formik.touched.imageName && (
                  <div className="text-red text-sm">{formik.errors.imageName}</div>
                )}
              </div>

              {/* Image Type */}
              <div className="flex flex-col gap-2">
                <label className="label">Image Type</label>
                <Dropdown
                  options={ImageType}
                  onSelect={(v, id) => {
                    setShowVal(v);
                    formik.setFieldValue("premium", id === "PREMIUM");
                  }}
                  selectedValue={showVal}
                />
              </div>

              {/* Background upload */}
              <div className="flex flex-col gap-2">
                <label className="label">Background</label>
                <label
                  onClick={() => bgFileInput.current.click()}
                  className="border-2 border-dashed border-blue rounded-6 flex items-center gap-2 px-4 py-2 cursor-pointer"
                >
                  <BiImageAdd className="text-blue text-2xl flex-shrink-0" />
                  <span className="text-white text-sm line-clamp-1">
                    {bgFile ? bgFile.name : bgFileName ? bgFileName : "Upload or drop a file right here"}
                  </span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  ref={bgFileInput}
                  onChange={handleBgChange}
                  className="hidden"
                />
                {bgError && <div className="text-red text-sm">{bgError}</div>}
              </div>

              {/* Wallpaper Preview */}
              <div className="relative">
                <button
                  type="button"
                  title="Reset all clock settings"
                  onClick={resetAllClockSettings}
                  className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70"
                >
                  <HiRefresh className="text-sm" />
                </button>
                <WallpaperCanvasPreview
                  ref={wallpaperPreviewRef}
                  bgSrc={bgImage}
                  fgSrc={fgPreviewUrl}
                  clockSettings={clockSettings}
                />
              </div>
            </div>

            {/* RIGHT: Category + Collection + Foreground + Clock/Date + Quick Panel + Hours/Minutes */}
            <div className="w-full lg:w-[55%] flex flex-col gap-4">

              <div className="flex flex-col gap-4 justify-start">
                <div className="flex flex-col gap-2">
                  <label className="label">Category</label>
                  <Dropdown
                    options={CategoryOption}
                    onSelect={(v, id) => {
                      setShowVal2(v);
                      setSelectedCatId(id);
                      formik.setFieldValue("categoryId", id);
                    }}
                    selectedValue={showVal2}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="label">Image Collection</label>
                  <Dropdown
                    options={CollectionOption}
                    onSelect={(v, id) => {
                      setShowVal3(v);
                      setSelectedColId(id);
                      formik.setFieldValue("collectionId", id);
                    }}
                    selectedValue={showVal3}
                  />
                </div>

                {/* Foreground upload */}
                <div className="flex flex-col gap-2">
                  <label className="label">Foreground</label>
                  <label
                    onClick={() => fgFileInput.current.click()}
                    className="border-2 border-dashed border-blue rounded-6 flex items-center gap-2 px-4 py-2 cursor-pointer"
                  >
                    <BiImageAdd className="text-blue text-2xl flex-shrink-0" />
                    <span className="text-white text-sm line-clamp-1">
                      {fgFile ? fgFile.name : fgPreviewUrl ? fgPreviewUrl.split("/").pop() || "foreground-image" : "Upload or drop a file right here"}
                    </span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fgFileInput}
                    onChange={handleFgChange}
                    className="hidden"
                  />
                  {fgError && <div className="text-red text-sm">{fgError}</div>}
                </div>
              </div>

              {/* Clock/Date tabs + Quick Panel + Hours/Minutes (page 0 only) */}
              {activePage === 0 && (
                <>
                  <ClockTopBar
                    clockSettings={clockSettings}
                    setClock={setClock}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    moveLeft={moveLeft}
                    moveRight={moveRight}
                    apiFontCategories={apiFontCategories}
                  />

                  {/* Quick Panel */}
                  <div className="bg-gray-800 rounded-10 p-4">
                    <h3 className="text-white text-sm font-semibold text-center mb-3">Quick Panel</h3>
                    <div className={`grid grid-cols-2 gap-x-6 gap-y-3 transition-opacity ${activeTab === "date" && !clockSettings.dateEnabled ? "opacity-40 pointer-events-none select-none" : "opacity-100 pointer-events-auto"}`}>
                      <Slider label="Font Size" value={activeTab === "date" ? clockSettings.dateFontSize : clockSettings.clockFontSize} onChange={(v) => setClock(activeTab === "date" ? "dateFontSize" : "clockFontSize", v)} onReset={() => resetQuickSettingFromApi(activeTab === "date" ? "dateFontSize" : "clockFontSize")} />
                      {activeTab === "date" ? (
                        <Slider label="Opacity" value={clockSettings.dateOpacity ?? 100} onChange={(v) => setClock("dateOpacity", v)} onReset={() => resetQuickSettingFromApi("dateOpacity")} resetValue={100} />
                      ) : (
                        <Slider label="Opacity" value={clockSettings.clockOpacity} onChange={(v) => setClock("clockOpacity", v)} onReset={() => resetQuickSettingFromApi("clockOpacity")} resetValue={45} />
                      )}
                      <Slider label="h-pos" value={activeTab === "date" ? clockSettings.dateHPos : clockSettings.clockHPos} onChange={(v) => setClock(activeTab === "date" ? "dateHPos" : "clockHPos", v)} onReset={() => resetQuickSettingFromApi(activeTab === "date" ? "dateHPos" : "clockHPos")} />
                      <Slider label="v-pos" value={activeTab === "date" ? clockSettings.dateVPos : clockSettings.clockVPos} onChange={(v) => setClock(activeTab === "date" ? "dateVPos" : "clockVPos", v)} onReset={() => resetQuickSettingFromApi(activeTab === "date" ? "dateVPos" : "clockVPos")} />
                      {activeTab === "clock" && (
                        <>
                          <SliderRaw label="Line Spacing" value={clockSettings.clockLineSpacing ?? 0} min={-5} max={5} step={0.01} resetValue={0} onChange={(v) => setClock("clockLineSpacing", v)} onReset={() => resetQuickSettingFromApi("clockLineSpacing")} />
                          <SliderRaw label="Letter Spacing" value={clockSettings.clockLetterSpacing ?? 0} min={-2} max={2} step={0.01} resetValue={0} onChange={(v) => setClock("clockLetterSpacing", v)} onReset={() => resetQuickSettingFromApi("clockLetterSpacing")} />
                        </>
                      )}
                      {activeTab === "date" && (
                        <div className="flex flex-col gap-1 w-full">
                          <div className="flex items-center justify-between">
                            <span className="text-white text-xs font-medium">Rotation</span>
                            <div className="flex items-center gap-1">
                              <span className="text-white text-xs">{clockSettings.dateRotation || 0}°</span>
                              <HiRefresh className="text-white text-sm cursor-pointer" onClick={() => resetQuickSettingFromApi("dateRotation")} />
                            </div>
                          </div>
                          <input
                            type="range" min={-180} max={180} step={1} value={clockSettings.dateRotation || 0}
                            onChange={(e) => setClock("dateRotation", Number(e.target.value))}
                            className="w-full h-1 appearance-none rounded-full cursor-pointer"
                            style={{ background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${(((clockSettings.dateRotation || 0) + 180) / 360) * 100}%, #4B5563 ${(((clockSettings.dateRotation || 0) + 180) / 360) * 100}%, #4B5563 100%)` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Hours/Minutes/Seconds Forward */}
                  <div className="bg-gray-800 rounded-10 p-4 flex flex-col gap-3">
                    <label className="flex items-center justify-between bg-gray-700 rounded-full px-4 py-3 cursor-pointer">
                      <span className="text-white text-sm font-medium">Hours Forward</span>
                      <Toggle checked={clockSettings.hoursForward} onChange={(v) => setClock("hoursForward", v)} />
                    </label>
                    <label className="flex items-center justify-between bg-gray-700 rounded-full px-4 py-3 cursor-pointer">
                      <span className="text-white text-sm font-medium">Minutes Forward</span>
                      <Toggle checked={clockSettings.minutesForward} onChange={(v) => setClock("minutesForward", v)} />
                    </label>
                    {clockSettings.showSeconds && (
                      <label className="flex items-center justify-between bg-gray-700 rounded-full px-4 py-3 cursor-pointer">
                        <span className="text-white text-sm font-medium">Seconds Forward</span>
                        <Toggle checked={clockSettings.secondsForward} onChange={(v) => setClock("secondsForward", v)} />
                      </label>
                    )}
                  </div>

                  {/* Time & Date selector panel */}
                  <div className="bg-gray-800 rounded-10 p-4 flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FaRegClock className="text-gray-400 text-base" />
                        <span className="text-white text-sm font-medium">Time</span>
                      </div>
                      <LiveClockTimePicker
                        value={clockSettings.previewTime || clockSettings.preveiwTime}
                        is24Hr={clockSettings.is24Hr}
                        showSeconds={clockSettings.showSeconds}
                        onChange={(val) => setClock("previewTime", val)}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FaRegCalendarAlt className="text-gray-400 text-base" />
                        <span className="text-white text-sm font-medium">Date</span>
                      </div>
                      <LiveClockDatePicker
                        value={clockSettings.previewDate || clockSettings.preveiwDate || getDefaultPreviewDate()}
                        onChange={(val) => setClock("previewDate", val)}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Effects + Shadow panel (page 2 only, in right column) */}
              {activePage === 2 && (
                <>
                  <h3 className="text-white text-sm font-semibold">Effects</h3>
                  <div className="bg-gray-800 rounded-10 p-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <span className="text-white text-sm">Border</span>
                        <Toggle checked={clockSettings.borderEnabled} onChange={(v) => setClock("borderEnabled", v)} />
                      </div>
                      <div className={`flex flex-col gap-3 transition-opacity ${clockSettings.borderEnabled ? "opacity-100 pointer-events-auto" : "opacity-40 pointer-events-none select-none"}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-white text-sm">Fill Border</span>
                          <Toggle checked={clockSettings.fillBorderEnabled} onChange={(v) => setClock("fillBorderEnabled", v)} />
                        </div>
                        <div>
                          <span className="text-white text-sm font-medium">Border Target</span>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            {BORDER_TARGET_OPTIONS
                              .filter((opt) => opt.value !== "Seconds" || clockSettings.showSeconds)
                              .map((opt) => {
                                const targets = Array.isArray(clockSettings.clockBorderTarget) && clockSettings.clockBorderTarget.length > 0
                                  ? clockSettings.clockBorderTarget
                                  : ["Full"];
                                const active = targets.includes(opt.value);
                                return (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => toggleBorderTarget(opt.value)}
                                    className={`px-3 py-1.5 rounded-6 text-xs font-medium border text-center transition-colors ${
                                      active
                                        ? "bg-blue border-blue text-white"
                                        : "bg-gray-700 border-gray-600 text-gray-300 hover:text-white"
                                    }`}
                                  >
                                    {opt.label}
                                  </button>
                                );
                              })}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-white text-sm">Border Color</span>
                          <div className="flex items-center gap-2">
                            <ColorPickerPopup color={clockSettings.borderColor} onChange={(val) => setClock("borderColor", val)} size="lg" showPipette />
                            <span className="text-white text-xs font-mono">{clockSettings.borderColor}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-1">
                          <SliderRaw label="Bor. Width" value={clockSettings.borderWidth} onChange={(v) => setClock("borderWidth", v)} min={0.1} max={2.0} step={0.1} resetValue={1.0} />
                          <SliderRaw label="Bor. Opacity" value={clockSettings.borderOpacity} onChange={(v) => setClock("borderOpacity", v)} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-white text-sm font-semibold">Shadow</h3>
                  <div className="bg-gray-800 rounded-10 p-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <span className="text-white text-sm">Enable Shadow</span>
                        <Toggle checked={clockSettings.shadowEnabled} onChange={(v) => setClock("shadowEnabled", v)} />
                      </div>
                      <div className={`flex flex-col gap-3 transition-opacity ${clockSettings.shadowEnabled ? "opacity-100 pointer-events-auto" : "opacity-40 pointer-events-none select-none"}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-white text-sm">Shadow Color</span>
                          <div className="flex items-center gap-2">
                            <ColorPickerPopup color={clockSettings.shadowColor} onChange={(val) => setClock("shadowColor", val)} size="lg" showPipette />
                            <span className="text-white text-xs font-mono">{clockSettings.shadowColor}</span>
                          </div>
                        </div>
                        <p className="text-gray-400 text-xs font-medium mt-1">Shadow Options</p>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                          <SliderRaw label="Sh. h-Offset" value={clockSettings.shadowHOffset} onChange={(v) => setClock("shadowHOffset", v)} min={-10.0} max={10.0} step={0.1} resetValue={0} />
                          <SliderRaw label="Sh. v-Offset" value={clockSettings.shadowVOffset} onChange={(v) => setClock("shadowVOffset", v)} min={-10.0} max={10.0} step={0.1} resetValue={0} />
                          <SliderRaw label="Sh. Blur" value={clockSettings.shadowBlur} onChange={(v) => setClock("shadowBlur", v)} min={0.0} max={5.0} step={0.1} resetValue={0} />
                          <SliderRaw label="Sh. Opacity" value={clockSettings.shadowOpacity} onChange={(v) => setClock("shadowOpacity", v)} />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Transform panel (page 1 only, in right column) */}
              {activePage === 1 && (
                <>
                  <h3 className="text-white text-sm font-semibold mb-0">Transform</h3>
                  <div className="bg-gray-800 rounded-10 p-4">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">

                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-xs font-medium">Rotation</span>
                          <div className="flex items-center gap-1">
                            <span className="text-white text-xs">{clockSettings.clockRotation}°</span>
                            <HiRefresh className="text-white text-sm cursor-pointer" onClick={() => resetQuickSettingFromApi("clockRotation")} />
                          </div>
                        </div>
                        <input type="range" min={-180} max={180} value={clockSettings.clockRotation}
                          onChange={(e) => setClock("clockRotation", Number(e.target.value))}
                          className="w-full h-1 appearance-none rounded-full cursor-pointer"
                          style={{ background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${((clockSettings.clockRotation + 180) / 360) * 100}%, #4B5563 ${((clockSettings.clockRotation + 180) / 360) * 100}%, #4B5563 100%)` }}
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-xs font-medium">persp-rot-X</span>
                          <div className="flex items-center gap-1">
                            <span className="text-white text-xs">{clockSettings.perspRotX}°</span>
                            <HiRefresh className="text-white text-sm cursor-pointer" onClick={() => resetQuickSettingFromApi("perspRotX")} />
                          </div>
                        </div>
                        <input type="range" min={-90} max={90} step={1} value={clockSettings.perspRotX}
                          onChange={(e) => setClock("perspRotX", Number(e.target.value))}
                          className="w-full h-1 appearance-none rounded-full cursor-pointer"
                          style={{ background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${((clockSettings.perspRotX + 90) / 180) * 100}%, #4B5563 ${((clockSettings.perspRotX + 90) / 180) * 100}%, #4B5563 100%)` }}
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-xs font-medium">persp-rot-Y</span>
                          <div className="flex items-center gap-1">
                            <span className="text-white text-xs">{clockSettings.perspRotY}°</span>
                            <HiRefresh className="text-white text-sm cursor-pointer" onClick={() => resetQuickSettingFromApi("perspRotY")} />
                          </div>
                        </div>
                        <input type="range" min={-90} max={90} step={1} value={clockSettings.perspRotY}
                          onChange={(e) => setClock("perspRotY", Number(e.target.value))}
                          className="w-full h-1 appearance-none rounded-full cursor-pointer"
                          style={{ background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${((clockSettings.perspRotY + 90) / 180) * 100}%, #4B5563 ${((clockSettings.perspRotY + 90) / 180) * 100}%, #4B5563 100%)` }}
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-xs font-medium">persp-rot-Z</span>
                          <div className="flex items-center gap-1">
                            <span className="text-white text-xs">{clockSettings.perspRotZ}°</span>
                            <HiRefresh className="text-white text-sm cursor-pointer" onClick={() => resetQuickSettingFromApi("perspRotZ")} />
                          </div>
                        </div>
                        <input type="range" min={-180} max={180} step={1} value={clockSettings.perspRotZ}
                          onChange={(e) => setClock("perspRotZ", Number(e.target.value))}
                          className="w-full h-1 appearance-none rounded-full cursor-pointer"
                          style={{ background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${((clockSettings.perspRotZ + 180) / 360) * 100}%, #4B5563 ${((clockSettings.perspRotZ + 180) / 360) * 100}%, #4B5563 100%)` }}
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-xs font-medium">h-Stretch</span>
                          <div className="flex items-center gap-1">
                            <span className="text-white text-xs">{clockSettings.hStretch.toFixed(2)}</span>
                            <HiRefresh className="text-white text-sm cursor-pointer" onClick={() => resetQuickSettingFromApi("hStretch")} />
                          </div>
                        </div>
                        <input type="range" min={0} max={2} step={0.01} value={clockSettings.hStretch}
                          onChange={(e) => setClock("hStretch", Number(e.target.value))}
                          className="w-full h-1 appearance-none rounded-full cursor-pointer"
                          style={{ background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${(clockSettings.hStretch / 2) * 100}%, #4B5563 ${(clockSettings.hStretch / 2) * 100}%, #4B5563 100%)` }}
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-xs font-medium">v-Stretch</span>
                          <div className="flex items-center gap-1">
                            <span className="text-white text-xs">{clockSettings.vStretch.toFixed(2)}</span>
                            <HiRefresh className="text-white text-sm cursor-pointer" onClick={() => resetQuickSettingFromApi("vStretch")} />
                          </div>
                        </div>
                        <input type="range" min={0} max={2} step={0.01} value={clockSettings.vStretch}
                          onChange={(e) => setClock("vStretch", Number(e.target.value))}
                          className="w-full h-1 appearance-none rounded-full cursor-pointer"
                          style={{ background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${(clockSettings.vStretch / 2) * 100}%, #4B5563 ${(clockSettings.vStretch / 2) * 100}%, #4B5563 100%)` }}
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-xs font-medium">h-Skew</span>
                          <div className="flex items-center gap-1">
                            <span className="text-white text-xs">{(clockSettings.hSkew || 0).toFixed(2)}</span>
                            <HiRefresh className="text-white text-sm cursor-pointer" onClick={() => resetQuickSettingFromApi("hSkew")} />
                          </div>
                        </div>
                        <div className="relative w-full">
                          <input type="range" min={-1} max={1} step={0.01} value={clockSettings.hSkew || 0}
                            onChange={(e) => setClock("hSkew", Number(e.target.value))}
                            className="w-full h-1 appearance-none rounded-full cursor-pointer"
                            style={{ background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${(((clockSettings.hSkew || 0) + 1) / 2) * 100}%, #4B5563 ${(((clockSettings.hSkew || 0) + 1) / 2) * 100}%, #4B5563 100%)` }}
                          />

                        </div>
                      </div>

                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-xs font-medium">v-Skew</span>
                          <div className="flex items-center gap-1">
                            <span className="text-white text-xs">{(clockSettings.vSkew || 0).toFixed(2)}</span>
                            <HiRefresh className="text-white text-sm cursor-pointer" onClick={() => resetQuickSettingFromApi("vSkew")} />
                          </div>
                        </div>
                        <div className="relative w-full">
                          <input type="range" min={-1} max={1} step={0.01} value={clockSettings.vSkew || 0}
                            onChange={(e) => setClock("vSkew", Number(e.target.value))}
                            className="w-full h-1 appearance-none rounded-full cursor-pointer"
                            style={{ background: `linear-gradient(to right, #0D47A1 0%, #0D47A1 ${(((clockSettings.vSkew || 0) + 1) / 2) * 100}%, #4B5563 ${(((clockSettings.vSkew || 0) + 1) / 2) * 100}%, #4B5563 100%)` }}
                          />

                        </div>
                      </div>

                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Full-width DotPagination + actions (page 1 only) ── */}
          {activePage === 1 && (
            <div className="w-full flex flex-col gap-2 mt-4">
              <DotPagination total={3} active={activePage} onChange={setActivePage} />
              <div className="flex items-center mt-2">
                <input type="checkbox" id="notification2" className="p-3" checked={notification} onChange={(e) => setNotification(e.target.checked)} />
                <label htmlFor="notification2" className="text-white ps-4 cursor-pointer">Send Notification</label>
              </div>
              <div className="flex justify-end gap-4 items-center mt-4">
                <button type="button" onClick={() => navigate(-1)} className="btn cancel">Cancel</button>
                <button type="submit" disabled={loader || formik.isSubmitting} className="btn save">{loader ? <Loader /> : "Save"}</button>
              </div>
            </div>
          )}

          {/* ── Full-width DotPagination + actions (page 2 only) ── */}
          {activePage === 2 && (
            <div className="w-full flex flex-col gap-2 mt-4">
              <DotPagination total={3} active={activePage} onChange={setActivePage} />
              <div className="flex items-center mt-2">
                <input type="checkbox" id="notification3" className="p-3" checked={notification} onChange={(e) => setNotification(e.target.checked)} />
                <label htmlFor="notification3" className="text-white ps-4 cursor-pointer">Send Notification</label>
              </div>
              <div className="flex justify-end gap-4 items-center mt-4">
                <button type="button" onClick={() => navigate(-1)} className="btn cancel">Cancel</button>
                <button type="submit" className="btn save">{loader ? <Loader /> : "Save"}</button>
              </div>
            </div>
          )}

          {/* ── Full-width Gradient panel (page 0, clock tab only) ── */}
          {activePage === 0 && activeTab === "clock" && (
            <div className="w-full mt-4">
              <div className="bg-gray-800 rounded-10 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white text-sm font-semibold">Gradient</h3>
                  <Toggle checked={clockSettings.gradientEnabled} onChange={(v) => setClock("gradientEnabled", v)} />
                </div>
                {clockSettings.gradientEnabled && (
                  <>
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-gray-400 text-xs mb-1">
                        <span className="text-white text-sm font-medium">Gradient Type</span>
                      </div>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between bg-gray-700 rounded-6 px-3 py-2"
                        onClick={() => {
                          const types = ["linear", "radial"];
                          const idx = types.indexOf(clockSettings.gradientType);
                          setClock("gradientType", types[(idx + 1) % types.length]);
                        }}
                      >
                        <span className="text-gray-400 text-sm capitalize">{clockSettings.gradientType}</span>
                        <IoChevronForward className="text-gray-400" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                      <AngleSlider label="Grad. Angle" value={clockSettings.gradAngle} onChange={(v) => setClock("gradAngle", v)} />
                      <SliderRaw label="Grad. Stop 2" value={clockSettings.gradStop2} onChange={(v) => setClock("gradStop2", v)} min={0} max={1} resetValue={1} />
                      <SliderRaw label="Grad. Stop 1" value={clockSettings.gradStop1} onChange={(v) => setClock("gradStop1", v)} min={0} max={1} step={0.01} resetValue={0} />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Full-width DotPagination + actions (page 0 only) ── */}
          {activePage === 0 && (
            <div className="w-full flex flex-col gap-2 mt-4">
              <DotPagination total={3} active={activePage} onChange={setActivePage} />
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="notification"
                  className="p-3"
                  checked={notification}
                  onChange={(e) => setNotification(e.target.checked)}
                />
                <label htmlFor="notification" className="text-white ps-4 cursor-pointer">
                  Send Notification
                </label>
              </div>
              <div className="flex justify-end gap-4 items-center mt-4">
                <button type="button" onClick={() => navigate(-1)} className="btn cancel">
                  Cancel
                </button>
                <button type="submit" className="btn save">
                  {loader ? <Loader /> : "Save"}
                </button>
              </div>
            </div>
          )}
        </form>
        <ToastContainer autoClose={1500} />
      </div>
    </>
  );
};

export default LiveClock;